import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { MapPin, Clock, Calendar, Building2, Euro, AlertTriangle, CheckCircle2, XCircle, Send, ArrowLeft, FolderKanban, Plus, Trash2, FileText, Pencil, Loader2 as Spinner, X, Users, Download, FileSpreadsheet, Sparkles, BarChart3, Zap, RefreshCw } from 'lucide-react';
import { SEOHead, jobPostingSchema } from '../components/SEOHead';
import { isJobExpired, jobStatusLabel, addDays, EXPIRED_TITLE, EXPIRED_DESCRIPTION } from '../lib/jobs';
import { queueApplication, pendingForJob, isNetworkError } from '../lib/offlineQueue';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_CATEGORY_LABELS = {
  plans_architecte: { label: 'Plans Architecte', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  cahier_charges: { label: 'Cahier des charges', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  plans_ingenieur: { label: 'Plans Ingénieur', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  metre: { label: 'Métré', color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const JobDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const flag = searchParams.get('reactivated');
    if (!flag) return;
    // Defer until after first paint so the global Toaster is mounted (child effects run before parent effects)
    const timer = setTimeout(() => {
      if (flag === '1') toast.success('Votre annonce a été prolongée de 30 jours et est à nouveau visible', { duration: 8000 });
      else toast.error('Lien de réactivation invalide ou expiré. Utilisez le bouton « Réactiver l\'annonce ».', { duration: 8000 });
      const next = new URLSearchParams(searchParams);
      next.delete('reactivated');
      setSearchParams(next, { replace: true });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchParams, setSearchParams]);
  const { user, isContractor, isCompany, isPremium, isAdmin } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewCount, setViewCount] = useState(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applicationData, setApplicationData] = useState({
    message: '',
    proposed_budget: ''
  });
  const [quoteLines, setQuoteLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deletingJob, setDeletingJob] = useState(false);
  const [applications, setApplications] = useState([]);
  const [updatingAppId, setUpdatingAppId] = useState(null);
  const [viewingApp, setViewingApp] = useState(null);
  const [queuedApp, setQueuedApp] = useState(() => pendingForJob(id));
  const [quickReply, setQuickReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    const sync = () => setQueuedApp(pendingForJob(id));
    window.addEventListener('bc-offline-queue-change', sync);
    return () => window.removeEventListener('bc-offline-queue-change', sync);
  }, [id]);

  const handleQuickReply = async () => {
    if (!quickReply.trim() || !viewingApp?.contractor_id) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/messages`, { recipient_id: viewingApp.contractor_id, content: quickReply.trim() }, { withCredentials: true });
      toast.success(`Message envoyé à ${viewingApp.contractor_name || 'ce sous-traitant'}`);
      setQuickReply('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Erreur lors de l'envoi du message");
    } finally {
      setSendingReply(false);
    }
  };
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [reactivateData, setReactivateData] = useState({ start_date: '', expires_at: addDays(30) });

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const payload = { status: 'open', expires_at: reactivateData.expires_at, reactivated_at: new Date().toISOString() };
      if (reactivateData.start_date) payload.start_date = reactivateData.start_date;
      const res = await axios.put(`${API}/jobs/${id}`, payload, { withCredentials: true });
      setJob(res.data);
      setReactivateOpen(false);
      toast.success(`Annonce réactivée jusqu'au ${new Date(reactivateData.expires_at).toLocaleDateString('fr-FR')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la réactivation');
    } finally {
      setReactivating(false);
    }
  };
  const [categorizedDocs, setCategorizedDocs] = useState([]);
  const [generatingAiQuote, setGeneratingAiQuote] = useState(false);
  const [aiQuoteNotes, setAiQuoteNotes] = useState('');
  const [staffInfo, setStaffInfo] = useState({
    employee_count: 0,
    onss_number: '',
    urssaf_number: '',
    has_limosa: false,
    has_onss: false,
    has_urssaf: false,
    detached_workers: []
  });

  const isJobOwner = user && job && (job.company_id === user.id || user.role === 'admin');

  const handleDeleteJob = async () => {
    if (!window.confirm('Supprimer ce chantier ? Cette action est irréversible.')) return;
    setDeletingJob(true);
    try {
      await axios.get(`${API}/jobs/${id}/delete`, { withCredentials: true });
      toast.success('Chantier supprimé');
      navigate('/jobs');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeletingJob(false);
    }
  };

  const addQuoteLine = () => {
    setQuoteLines([...quoteLines, { description: '', quantity: 1, unit: 'pce', unit_price: 0 }]);
  };

  const updateQuoteLine = (index, field, value) => {
    const updated = [...quoteLines];
    updated[index][field] = value;
    setQuoteLines(updated);
  };

  const removeQuoteLine = (index) => {
    setQuoteLines(quoteLines.filter((_, i) => i !== index));
  };

  const quoteTotal = quoteLines.reduce((sum, line) => sum + (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0), 0);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await axios.get(`${API}/jobs/${id}`);
        setJob(res.data);
      } catch (error) {
        console.error('Error fetching job:', error);
        toast.error('Chantier non trouvé');
        navigate('/jobs');
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id, navigate]);

  // Check free quota for contractors
  useEffect(() => {
    if (!user || isPremium || isAdmin) { setViewCount(0); return; }
    const checkQuota = async () => {
      try {
        const res = await axios.get(`${API}/my-applications`, { withCredentials: true });
        setViewCount((res.data || []).length);
      } catch (e) { setViewCount(0); }
    };
    if (isContractor) checkQuota();
    else setViewCount(0);
  }, [user, isPremium, isAdmin, isContractor]);

  // Fetch categorized documents
  useEffect(() => {
    if (!job) return;
    const fetchDocs = async () => {
      try {
        const res = await axios.get(`${API}/jobs/${id}/categorized-docs`);
        setCategorizedDocs(res.data || []);
      } catch (err) {
        console.error('Error fetching docs:', err);
      }
    };
    fetchDocs();
  }, [job, id]);

  // Fetch applications if owner
  useEffect(() => {
    if (!job || !user) return;
    if (job.company_id !== user.id && user.role !== 'admin') return;
    const fetchApps = async () => {
      try {
        const res = await axios.get(`${API}/applications/job/${id}`, { withCredentials: true });
        setApplications(res.data || []);
      } catch (err) {
        console.error('Error fetching applications:', err);
      }
    };
    fetchApps();
  }, [job, user, id]);

  const handleUpdateAppStatus = async (appId, status) => {
    setUpdatingAppId(appId);
    try {
      await axios.put(`${API}/applications/${appId}/status`, { status }, { withCredentials: true });
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status } : a));
      toast.success(status === 'accepted' ? 'Candidature acceptée' : 'Candidature refusée');
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingAppId(null);
    }
  };

  // AI Quote Generation from Métré
  const handleGenerateAiQuote = useCallback(async () => {
    if (!job) return;
    setGeneratingAiQuote(true);
    setAiQuoteNotes('');

    try {
      // Check if there's a métré document, parse it first
      const metreDoc = categorizedDocs.find(d => d.category === 'metre');
      let metreLines = [];

      if (metreDoc) {
        const parseRes = await axios.get(`${API}/jobs/${id}/parse-metre`, { withCredentials: true });
        metreLines = parseRes.data.lines || [];
      }

      if (metreLines.length === 0) {
        toast.error('Aucun métré trouvé. Ajoutez des lignes manuellement ou demandez au donneur d\'ordre d\'uploader un métré XLSX.');
        setGeneratingAiQuote(false);
        return;
      }

      // Call AI to estimate prices
      const aiRes = await axios.post(`${API}/ai/generate-quote-from-metre`, {
        job_id: id,
        metre_lines: metreLines,
        description: job.description || '',
        trade_categories: job.trade_categories || [job.trade_category],
        city: job.city || '',
        country: job.country || 'Belgium'
      }, { withCredentials: true });

      const result = aiRes.data;

      if (result.lines && result.lines.length > 0) {
        setQuoteLines(result.lines.map(l => ({
          description: l.description || '',
          quantity: l.quantity || 0,
          unit: l.unit || 'pce',
          unit_price: l.unit_price || 0
        })));
        setAiQuoteNotes(result.notes || '');
        toast.success(result.ai_powered
          ? `Devis IA généré (${result.lines.length} postes, confiance: ${result.confidence})`
          : `Devis estimé (${result.lines.length} postes) - ajustez les prix`
        );
      }
    } catch (err) {
      console.error('AI quote error:', err);
      toast.error('Erreur lors de la génération du devis IA');
    } finally {
      setGeneratingAiQuote(false);
    }
  }, [job, categorizedDocs, id]);

  const handleApply = async () => {
    if (!user) {
      toast.error('Veuillez vous connecter pour postuler');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    const payload = {
        job_id: id,
        message: applicationData.message,
        proposed_budget: applicationData.proposed_budget ? parseFloat(applicationData.proposed_budget) : (quoteTotal > 0 ? quoteTotal : null),
        quote_lines: quoteLines.length > 0 ? quoteLines.map(l => ({
          description: l.description,
          quantity: parseFloat(l.quantity) || 0,
          unit: l.unit,
          unit_price: parseFloat(l.unit_price) || 0,
          total: (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0)
        })) : null,
        staff_info: {
          employee_count: staffInfo.employee_count,
          onss_number: staffInfo.onss_number,
          has_onss: staffInfo.has_onss,
          has_urssaf: staffInfo.has_urssaf,
          has_limosa: staffInfo.has_limosa,
          detached_workers: staffInfo.detached_workers
        }
    };
    if (!navigator.onLine) {
      queueApplication(payload, job?.title);
      setQueuedApp(pendingForJob(id));
      toast.info('Vous êtes hors ligne : candidature enregistrée sur cet appareil, elle sera envoyée automatiquement dès le retour du réseau', { duration: 8000 });
      setApplyDialogOpen(false);
      setApplicationData({ message: '', proposed_budget: '' });
      setSubmitting(false);
      return;
    }
    try {
      await axios.post(`${API}/applications`, payload, { withCredentials: true });
      
      toast.success('Candidature envoyée avec succès!');
      setApplyDialogOpen(false);
      setApplicationData({ message: '', proposed_budget: '' });
    } catch (error) {
      if (isNetworkError(error)) {
        queueApplication(payload, job?.title);
        setQueuedApp(pendingForJob(id));
        toast.info('Pas de réseau : candidature enregistrée sur cet appareil, elle sera envoyée automatiquement dès le retour de la connexion', { duration: 8000 });
        setApplyDialogOpen(false);
        setApplicationData({ message: '', proposed_budget: '' });
        return;
      }
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de l\'envoi de la candidature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A8A]"></div>
      </div>
    );
  }

  if (!job) return null;

  if (isJobExpired(job) && !isJobOwner) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" onClick={() => navigate('/jobs')} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux chantiers
          </Button>
          <Card className="mb-6 opacity-90" data-testid="job-expired-card">
            <CardContent className="p-8 text-center">
              <Badge className="bg-red-100 text-red-700 border border-red-300 mb-4" data-testid="job-status-badge">Expiré</Badge>
              <h1 className="font-heading font-bold text-3xl text-[#6B7280] mb-3" data-testid="job-title">{EXPIRED_TITLE}</h1>
              <p className="text-[#4B5563] mb-6">{EXPIRED_DESCRIPTION}</p>
              <div className="flex items-center justify-center gap-2 text-[#6B7280] mb-8">
                <MapPin className="w-5 h-5" />
                <span>{job.city}, {job.country}</span>
              </div>
              <Link to="/jobs">
                <Button className="btn-primary" data-testid="expired-back-to-jobs">Voir les chantiers ouverts</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const hasMetre = categorizedDocs.some(d => d.category === 'metre');

  // Access control: non-logged users or free contractors who used their quota
  const canViewFull = !!user && (
    isJobOwner ||
    user.role === 'admin' ||
    isPremium ||
    isCompany ||
    (isContractor && viewCount !== null && viewCount < 1)
  );

  if (!user) {
    // Not logged in - show preview only
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" onClick={() => navigate('/jobs')} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux chantiers
          </Button>
          <Card className="mb-6" data-testid="job-preview-card">
            <CardContent className="p-8">
              <div className="flex flex-wrap items-start gap-2 mb-4">
                {(job.trade_categories && job.trade_categories.length > 0 ? job.trade_categories : [job.trade_category]).filter(Boolean).map(tc => (
                  <Badge key={tc} className="badge-trade">{t(`trades.${tc}`) || tc}</Badge>
                ))}
              </div>
              <h1 className="font-heading font-bold text-3xl text-[#111827] mb-4">{job.title}</h1>
              <p className="text-[#374151] whitespace-pre-wrap mb-6">{job.description}</p>
              <div className="flex items-center gap-2 text-[#4B5563] mb-8">
                <MapPin className="w-5 h-5 text-[#1E3A8A]" />
                <span className="font-medium">{job.city}, {job.country}</span>
              </div>
              <div className="p-6 bg-[#1E3A8A]/5 border-2 border-[#1E3A8A]/20 rounded-lg text-center" data-testid="login-gate">
                <AlertTriangle className="w-8 h-8 text-[#1E3A8A] mx-auto mb-3" />
                <h3 className="font-heading font-bold text-lg text-[#111827] mb-2">Inscrivez-vous pour voir le dossier complet</h3>
                <p className="text-sm text-[#4B5563] mb-4">Budget, documents, photos, dates et possibilité de postuler.</p>
                <div className="flex gap-3 justify-center">
                  <Link to="/register"><Button className="btn-primary">Créer un compte</Button></Link>
                  <Link to="/login"><Button variant="outline">Se connecter</Button></Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!canViewFull) {
    // Logged in but free quota exhausted
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" onClick={() => navigate('/jobs')} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux chantiers
          </Button>
          <Card className="mb-6" data-testid="job-preview-card">
            <CardContent className="p-8">
              <div className="flex flex-wrap items-start gap-2 mb-4">
                {(job.trade_categories && job.trade_categories.length > 0 ? job.trade_categories : [job.trade_category]).filter(Boolean).map(tc => (
                  <Badge key={tc} className="badge-trade">{t(`trades.${tc}`) || tc}</Badge>
                ))}
              </div>
              <h1 className="font-heading font-bold text-3xl text-[#111827] mb-4">{job.title}</h1>
              <p className="text-[#374151] whitespace-pre-wrap mb-6">{job.description}</p>
              <div className="flex items-center gap-2 text-[#4B5563] mb-8">
                <MapPin className="w-5 h-5 text-[#1E3A8A]" />
                <span className="font-medium">{job.city}, {job.country}</span>
              </div>
              <div className="p-6 bg-[#F59E0B]/10 border-2 border-[#F59E0B]/30 rounded-lg text-center" data-testid="premium-gate">
                <Zap className="w-8 h-8 text-[#F59E0B] mx-auto mb-3" />
                <h3 className="font-heading font-bold text-lg text-[#111827] mb-2">Votre dossier gratuit a été utilisé</h3>
                <p className="text-sm text-[#4B5563] mb-4">Passez au Premium pour accéder à tous les dossiers de chantiers, postuler en illimité et contacter les donneurs d'ordre.</p>
                <Link to="/dashboard/subscription"><Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white">Passer au Premium</Button></Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/jobs')} className="mb-6" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux chantiers
        </Button>

        {/* SEO Dynamic Meta */}
        <SEOHead
          title={`${job.title} - ${job.city}`}
          description={`${job.description?.substring(0, 155)}...`}
          path={`/jobs/${id}`}
          type="article"
          jsonLd={jobPostingSchema(job)}
        />

        {/* Main Content */}
        <Card className="mb-6" data-testid="job-detail-card">
          <CardContent className="p-8">
            {/* Header */}
            <div className="flex flex-wrap items-start gap-2 mb-4">
              {(job.trade_categories && job.trade_categories.length > 0
                ? job.trade_categories
                : [job.trade_category]
              ).filter(Boolean).map(tc => (
                <Badge key={tc} className="badge-trade">{t(`trades.${tc}`) || tc}</Badge>
              ))}
              {job.is_urgent && <Badge className="badge-urgent">{t('common.urgent')}</Badge>}
              {(() => { const s = jobStatusLabel(job); return <Badge className={s.className} data-testid="job-status-badge">{s.label}</Badge>; })()}
              {job.expires_at && !isJobExpired(job) && job.status === 'open' && (
                <span className="text-xs text-[#6B7280] self-center" data-testid="job-expires-at">Visible jusqu'au {new Date(job.expires_at).toLocaleDateString('fr-FR')}</span>
              )}
            </div>

            <h1 className="font-heading font-bold text-3xl text-[#111827] mb-4" data-testid="job-title">
              {job.title}
            </h1>

            {/* Owner Actions */}
            {isJobOwner && (
              <div className="flex gap-3 mb-4 flex-wrap">
                {(isJobExpired(job) || job.status === 'closed') && (
                  <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setReactivateOpen(true)} data-testid="detail-reactivate-job">
                    <RefreshCw className="w-4 h-4" /> Réactiver l'annonce
                  </Button>
                )}
                <Button variant="outline" className="gap-2 border-[#1E3A8A]/30 text-[#1E3A8A]" onClick={() => navigate(`/dashboard/edit-job/${id}`)} data-testid="detail-edit-job">
                  <Pencil className="w-4 h-4" /> Modifier
                </Button>
                <Button variant="outline" className="gap-2 border-red-300 text-red-600 hover:bg-red-50" onClick={handleDeleteJob} disabled={deletingJob} data-testid="detail-delete-job">
                  {deletingJob ? <Spinner className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Supprimer
                </Button>
              </div>
            )}

            {/* Company Info */}
            <div className="flex items-center gap-2 text-[#4B5563] mb-6">
              <Building2 className="w-5 h-5 text-[#1E3A8A]" />
              <span className="font-medium">{job.company_name}</span>
            </div>

            {/* Job Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-[#F9FAFB] p-4 rounded-md">
                <p className="text-xs text-[#4B5563] mb-1">Localisation</p>
                <p className="font-medium flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-[#1E3A8A]" />
                  {job.city}, {job.country}
                </p>
              </div>
              <div className="bg-[#F9FAFB] p-4 rounded-md">
                <p className="text-xs text-[#4B5563] mb-1">Date de début</p>
                <p className="font-medium flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-[#1E3A8A]" />
                  {job.start_date}
                </p>
              </div>
              <div className="bg-[#F9FAFB] p-4 rounded-md">
                <p className="text-xs text-[#4B5563] mb-1">Durée</p>
                <p className="font-medium flex items-center gap-1">
                  <Clock className="w-4 h-4 text-[#1E3A8A]" />
                  {job.duration}
                </p>
              </div>
              <div className="bg-[#F9FAFB] p-4 rounded-md">
                <p className="text-xs text-[#4B5563] mb-1">Budget</p>
                <p className="font-bold text-[#1E3A8A] flex items-center gap-1">
                  <Euro className="w-4 h-4" />
                  {job.budget_min || job.budget_max 
                    ? `${job.budget_min?.toLocaleString() || '—'} - ${job.budget_max?.toLocaleString() || '—'}`
                    : 'Sur devis'}
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="font-heading font-bold text-xl mb-4">Description du chantier</h2>
              <p className="text-[#374151] whitespace-pre-wrap" data-testid="job-description">{job.description}</p>
            </div>

            {/* Categorized Documents */}
            {categorizedDocs.length > 0 && (
              <div className="mb-8" data-testid="job-documents-section">
                <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#1E3A8A]" />
                  Documents du chantier
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {categorizedDocs.map((doc) => {
                    const catInfo = DOC_CATEGORY_LABELS[doc.category] || { label: doc.category, color: 'bg-gray-100 text-gray-700 border-gray-200' };
                    const isXlsx = doc.original_filename?.endsWith('.xlsx') || doc.original_filename?.endsWith('.xls');
                    return (
                      <div key={doc.id} className={`flex items-center gap-3 p-3 rounded-lg border ${catInfo.color}`} data-testid={`doc-${doc.id}`}>
                        {isXlsx ? (
                          <FileSpreadsheet className="w-5 h-5 flex-shrink-0" />
                        ) : (
                          <FileText className="w-5 h-5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                          <p className="text-xs opacity-75">{catInfo.label} - {(doc.size / 1024).toFixed(0)} KB</p>
                        </div>
                        <a
                          href={`${API}/jobs/${id}/categorized-docs/${doc.id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0"
                        >
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`download-doc-${doc.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Photos */}
            {job.photos && job.photos.length > 0 && (
              <div className="mb-8">
                <h2 className="font-heading font-bold text-xl mb-4">Photos</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {job.photos.map((photo, index) => (
                    <img loading="lazy"
                      key={photo} 
                      src={photo} 
                      alt={`Photo ${index + 1}`} 
                      className="rounded-md w-full h-32 object-cover"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Apply Button */}
            {job.status === 'open' && (
              <div className="border-t border-[#1E3A8A]/10 pt-6">
                {queuedApp && (
                  <div className="mb-4 p-4 bg-[#1E3A8A]/5 border border-[#1E3A8A]/20 rounded-lg flex items-start gap-3" data-testid="queued-application-notice">
                    <Clock className="w-5 h-5 text-[#1E3A8A] mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-semibold text-[#1E3A8A]">Candidature en attente d'envoi</p>
                      <p className="text-[#4B5563]">Rédigée hors ligne le {new Date(queuedApp.queued_at).toLocaleString('fr-FR')}. Elle sera envoyée automatiquement dès que la connexion sera rétablie.</p>
                    </div>
                  </div>
                )}
                {isContractor ? (
                  !(user?.vat_number?.trim()) ? (
                    <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg flex items-start gap-3" data-testid="vat-warning-apply">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-red-700">Numéro de TVA ou SIRET obligatoire</p>
                        <p className="text-sm text-red-600 mt-1">Vous devez renseigner votre numéro de TVA ou SIRET dans votre profil avant de postuler.</p>
                        <Button variant="outline" className="mt-2 border-red-300 text-red-700 hover:bg-red-100" onClick={() => navigate('/dashboard/profile')}>
                          Compléter mon profil
                        </Button>
                      </div>
                    </div>
                  ) : (
                  <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="btn-accent w-full md:w-auto text-lg px-8 py-6" data-testid="apply-button">
                        <Send className="w-5 h-5 mr-2" />
                        {t('jobs.applyNow')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="font-heading">Postuler à ce chantier</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>{t('jobs.applicationMessage')}</Label>
                          <Textarea
                            placeholder="Présentez votre entreprise et expliquez pourquoi vous êtes le bon choix pour ce chantier..."
                            value={applicationData.message}
                            onChange={(e) => setApplicationData({...applicationData, message: e.target.value})}
                            className="min-h-[120px] input-field"
                            data-testid="application-message"
                          />
                        </div>

                        {/* AI Quote from Métré */}
                        {hasMetre && (
                          <div className="p-3 bg-gradient-to-r from-[#1E3A8A]/5 to-[#F59E0B]/5 border border-[#1E3A8A]/20 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-[#F59E0B]" />
                                <div>
                                  <p className="text-sm font-semibold text-[#111827]">Devis IA depuis le métré</p>
                                  <p className="text-xs text-[#6B7280]">L'IA pré-remplit les prix du marché basés sur le métré du chantier</p>
                                </div>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                className="gap-1 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white"
                                onClick={handleGenerateAiQuote}
                                disabled={generatingAiQuote}
                                data-testid="generate-ai-quote"
                              >
                                {generatingAiQuote ? (
                                  <><Spinner className="w-4 h-4 animate-spin" /> Analyse...</>
                                ) : (
                                  <><Sparkles className="w-4 h-4" /> Générer</>
                                )}
                              </Button>
                            </div>
                            {aiQuoteNotes && (
                              <p className="text-xs text-[#6B7280] mt-2 italic bg-white/50 p-2 rounded">{aiQuoteNotes}</p>
                            )}
                          </div>
                        )}

                        {/* Quote Lines Builder */}
                        <div className="border rounded-lg p-4 bg-[#F9FAFB]">
                          <div className="flex items-center justify-between mb-3">
                            <Label className="flex items-center gap-2 text-base font-semibold">
                              <FileText className="w-4 h-4 text-[#1E3A8A]" />
                              Devis détaillé {quoteLines.length > 0 ? `(${quoteLines.length} postes)` : '(optionnel)'}
                            </Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={addQuoteLine}
                              className="gap-1 text-xs"
                              data-testid="add-quote-line"
                            >
                              <Plus className="w-3 h-3" /> Ajouter une ligne
                            </Button>
                          </div>

                          {quoteLines.length > 0 ? (
                            <div className="space-y-2">
                              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-[#6B7280] px-1">
                                <div className="col-span-5">Description</div>
                                <div className="col-span-2">Qté</div>
                                <div className="col-span-2">Prix unit.</div>
                                <div className="col-span-2 text-right">Total</div>
                                <div className="col-span-1"></div>
                              </div>
                              {quoteLines.map((line, idx) => (
                                <div key={`quote-${idx}-${line.description}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`quote-line-${idx}`}>
                                  <Input
                                    className="col-span-5 text-sm h-8"
                                    placeholder="Ex: Pose carrelage"
                                    value={line.description}
                                    onChange={(e) => updateQuoteLine(idx, 'description', e.target.value)}
                                  />
                                  <Input
                                    className="col-span-2 text-sm h-8"
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={line.quantity}
                                    onChange={(e) => updateQuoteLine(idx, 'quantity', e.target.value)}
                                  />
                                  <Input
                                    className="col-span-2 text-sm h-8"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="€"
                                    value={line.unit_price}
                                    onChange={(e) => updateQuoteLine(idx, 'unit_price', e.target.value)}
                                  />
                                  <div className="col-span-2 text-right text-sm font-medium">
                                    {((parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0)).toFixed(2)}€
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="col-span-1 h-8 w-8 p-0 text-red-400 hover:text-red-600"
                                    onClick={() => removeQuoteLine(idx)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                              <div className="flex justify-end items-center gap-2 pt-2 border-t">
                                <span className="text-sm font-semibold text-[#111827]">Total HTVA:</span>
                                <span className="text-lg font-bold text-[#1E3A8A]" data-testid="quote-total">{quoteTotal.toFixed(2)}€</span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-[#9CA3AF] text-center py-2">
                              {hasMetre
                                ? 'Cliquez "Générer" ci-dessus pour pré-remplir avec les prix du marché, ou ajoutez manuellement'
                                : 'Ajoutez des lignes pour créer un devis détaillé joint à votre candidature'}
                            </p>
                          )}
                        </div>

                        {quoteLines.length === 0 && (
                          <div>
                            <Label>{t('jobs.proposedBudget')} (optionnel)</Label>
                            <Input
                              type="number"
                              placeholder="Votre proposition de budget global"
                              value={applicationData.proposed_budget}
                              onChange={(e) => setApplicationData({...applicationData, proposed_budget: e.target.value})}
                              className="input-field"
                              data-testid="proposed-budget"
                            />
                          </div>
                        )}

                        {/* Personnel & Conformité */}
                        <div className="border rounded-lg p-4 bg-[#F9FAFB]">
                          <Label className="flex items-center gap-2 text-base font-semibold mb-3">
                            <Users className="w-4 h-4 text-[#1E3A8A]" />
                            Personnel & Conformité
                          </Label>
                          
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <Label className="text-xs">Nombre de personnel sur chantier</Label>
                              <Input type="number" min="0" value={staffInfo.employee_count} onChange={(e) => setStaffInfo(s => ({...s, employee_count: parseInt(e.target.value) || 0}))} className="input-field h-9" data-testid="staff-employee-count" />
                            </div>
                            <div>
                              <Label className="text-xs">N° ONSS</Label>
                              <Input value={staffInfo.onss_number} onChange={(e) => setStaffInfo(s => ({...s, onss_number: e.target.value}))} placeholder="123-4567890-12" className="input-field h-9" data-testid="staff-onss" />
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-4 mb-3">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input type="checkbox" checked={staffInfo.has_onss} onChange={(e) => setStaffInfo(s => ({...s, has_onss: e.target.checked}))} className="w-4 h-4 accent-emerald-500" data-testid="staff-has-onss" />
                              ONSS en règle
                            </label>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input type="checkbox" checked={staffInfo.has_urssaf} onChange={(e) => setStaffInfo(s => ({...s, has_urssaf: e.target.checked}))} className="w-4 h-4 accent-emerald-500" data-testid="staff-has-urssaf" />
                              URSSAF en règle
                            </label>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input type="checkbox" checked={staffInfo.has_limosa} onChange={(e) => setStaffInfo(s => ({...s, has_limosa: e.target.checked}))} className="w-4 h-4 accent-emerald-500" data-testid="staff-has-limosa" />
                              Limosa fournie
                            </label>
                          </div>

                          <div className="border-t pt-3 mt-2">
                            <Label className="text-xs font-semibold mb-2 block">Personnel détaché par nationalité</Label>
                            {staffInfo.detached_workers.length > 0 && (
                              <div className="space-y-1 mb-2">
                                {staffInfo.detached_workers.map((dw, idx) => (
                                  <div key={`${dw.country}-${dw.count}-${idx}`} className="flex items-center gap-2 bg-white p-2 rounded border text-sm">
                                    <Badge className="bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs">{dw.country}</Badge>
                                    <span className="flex-1">{dw.count} ouvrier{dw.count > 1 ? 's' : ''}</span>
                                    <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setStaffInfo(s => ({...s, detached_workers: s.detached_workers.filter((_, i) => i !== idx)}))}>
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex items-end gap-2">
                              <select id="app-detached-country" className="input-field text-sm h-9 flex-1" defaultValue="" data-testid="app-detached-country">
                                <option value="" disabled>Pays</option>
                                <option value="Belgique">Belgique</option>
                                <option value="Luxembourg">Luxembourg</option>
                                <option value="France">France</option>
                                <option value="Allemagne">Allemagne</option>
                                <option value="Pays-Bas">Pays-Bas</option>
                                <option value="Pologne">Pologne</option>
                                <option value="Roumanie">Roumanie</option>
                                <option value="Portugal">Portugal</option>
                                <option value="Italie">Italie</option>
                                <option value="Espagne">Espagne</option>
                                <option value="Autre UE">Autre UE</option>
                                <option value="Hors UE">Hors UE</option>
                              </select>
                              <Input id="app-detached-count" type="number" min="1" placeholder="Nb" className="input-field h-9 w-16" data-testid="app-detached-count" />
                              <Button type="button" variant="outline" size="sm" className="h-9 text-xs gap-1" onClick={() => {
                                const cEl = document.getElementById('app-detached-country');
                                const nEl = document.getElementById('app-detached-count');
                                if (!cEl?.value) return toast.error('Sélectionnez un pays');
                                if (!parseInt(nEl?.value)) return toast.error("Indiquez le nombre d'ouvriers");
                                setStaffInfo(s => ({...s, detached_workers: [...s.detached_workers, {country: cEl.value, count: parseInt(nEl.value)}]}));
                                cEl.value = ''; nEl.value = '';
                              }} data-testid="app-add-detached">
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                            <p className="text-xs text-amber-700 mt-2 bg-amber-50 p-2 rounded border border-amber-200">
                              <AlertTriangle className="w-3 h-3 inline mr-1" />
                              Déclaration <strong>Limosa</strong> obligatoire pour tout personnel détaché en Belgique.
                            </p>
                          </div>
                        </div>

                        <Button 
                          className="btn-primary w-full" 
                          onClick={handleApply}
                          disabled={!applicationData.message || submitting}
                          data-testid="submit-application"
                        >
                          {submitting ? 'Envoi en cours...' : (quoteLines.length > 0 ? `Envoyer candidature + devis (${quoteTotal.toFixed(2)}€)` : 'Envoyer ma candidature')}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  )
                ) : user ? (
                  <p className="text-[#4B5563]">Seuls les sous-traitants peuvent postuler aux chantiers.</p>
                ) : (
                  <Link to="/login">
                    <Button className="btn-primary" data-testid="login-to-apply">
                      Connectez-vous pour postuler
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Candidatures reçues - visible par le donneur d'ordre */}
        {isJobOwner && applications.length > 0 && (
          <Card className="mb-6" data-testid="received-applications">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1E3A8A]" />
                Candidatures reçues ({applications.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {applications.map((app) => {
                const si = app.staff_info || {};
                return (
                <div key={app.id} className="p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]" data-testid={`received-app-${app.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-semibold text-[#111827]">{app.contractor_name || 'Sous-traitant'}</h4>
                        {app.status === 'accepted' && <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">Engagé</Badge>}
                        {app.status === 'rejected' && <Badge className="bg-red-100 text-red-700 border border-red-300">Refusé</Badge>}
                        {app.status === 'pending' && <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-300">En attente</Badge>}
                      </div>
                      <button
                        type="button"
                        onClick={() => setViewingApp(app)}
                        className="text-left w-full group"
                        title="Cliquer pour lire la candidature complète"
                        data-testid={`open-app-message-${app.id}`}
                      >
                        <p className="text-sm text-[#6B7280] line-clamp-2 whitespace-pre-line group-hover:text-[#374151]">{app.message}</p>
                        <span className="text-xs font-medium text-[#1E3A8A] group-hover:underline">Lire la candidature complète</span>
                      </button>
                      {app.proposed_budget && (
                        <p className="text-sm font-medium text-[#1E3A8A] mt-1">Budget proposé : {app.proposed_budget}€</p>
                      )}

                      {/* Detailed Quote View */}
                      {app.quote_lines && app.quote_lines.length > 0 && (
                        <div className="mt-3 p-3 bg-white rounded border border-[#E5E7EB]">
                          <p className="text-xs font-semibold text-[#374151] mb-2 flex items-center gap-1">
                            <FileText className="w-3 h-3 text-[#1E3A8A]" />
                            Devis détaillé ({app.quote_lines.length} postes)
                          </p>
                          <div className="space-y-1">
                            <div className="grid grid-cols-12 gap-1 text-xs font-medium text-[#9CA3AF]">
                              <div className="col-span-5">Description</div>
                              <div className="col-span-2">Qté</div>
                              <div className="col-span-2">Unit.</div>
                              <div className="col-span-3 text-right">Total</div>
                            </div>
                            {app.quote_lines.map((ql, qlIdx) => (
                              <div key={qlIdx} className="grid grid-cols-12 gap-1 text-xs text-[#374151]">
                                <div className="col-span-5 truncate">{ql.description}</div>
                                <div className="col-span-2">{ql.quantity} {ql.unit}</div>
                                <div className="col-span-2">{ql.unit_price?.toFixed(2)}€</div>
                                <div className="col-span-3 text-right font-medium">{(ql.total || 0).toFixed(2)}€</div>
                              </div>
                            ))}
                            <div className="border-t pt-1 mt-1 flex justify-end">
                              <span className="text-sm font-bold text-[#1E3A8A]">
                                Total: {app.quote_lines.reduce((sum, l) => sum + (l.total || 0), 0).toFixed(2)}€
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Personnel & Conformité */}
                      {(si.employee_count > 0 || si.detached_workers?.length > 0 || si.has_onss || si.has_urssaf || si.has_limosa) && (
                        <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
                          <p className="text-xs font-semibold text-[#374151] mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> Personnel : {si.employee_count || 0} personne{(si.employee_count || 0) > 1 ? 's' : ''}</p>
                          <div className="flex flex-wrap gap-2">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${si.has_onss ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-testid={`app-onss-status-${app.id}`}>
                              {si.has_onss ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                              ONSS {si.onss_number ? `(${si.onss_number})` : ''}
                            </span>
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${si.has_urssaf ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-testid={`app-urssaf-status-${app.id}`}>
                              {si.has_urssaf ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                              URSSAF
                            </span>
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${si.has_limosa ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-testid={`app-limosa-status-${app.id}`}>
                              {si.has_limosa ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                              Limosa
                            </span>
                          </div>
                          {si.detached_workers && si.detached_workers.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              <span className="text-xs text-[#6B7280]">Détachés :</span>
                              {si.detached_workers.map((dw, i) => (
                                <Badge key={`${dw.country}-${dw.count}`} className="bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs">{dw.country} ({dw.count})</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <p className="text-xs text-[#9CA3AF] mt-2">{new Date(app.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {app.status === 'pending' ? (
                        <>
                          <Button
                            size="sm"
                            className="bg-emerald-500 hover:bg-emerald-600 text-white h-10 w-10 p-0"
                            onClick={() => handleUpdateAppStatus(app.id, 'accepted')}
                            disabled={updatingAppId === app.id}
                            data-testid={`accept-app-${app.id}`}
                            title="Accepter"
                          >
                            <CheckCircle2 className="w-5 h-5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50 h-10 w-10 p-0"
                            onClick={() => handleUpdateAppStatus(app.id, 'rejected')}
                            disabled={updatingAppId === app.id}
                            data-testid={`reject-app-${app.id}`}
                            title="Refuser"
                          >
                            <XCircle className="w-5 h-5" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[#6B7280]"
                          onClick={() => handleUpdateAppStatus(app.id, 'pending')}
                          disabled={updatingAppId === app.id}
                          data-testid={`reset-app-${app.id}`}
                        >
                          Remettre en attente
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Dialog: réactivation de l'annonce */}
        <Dialog open={reactivateOpen} onOpenChange={setReactivateOpen}>
          <DialogContent className="max-w-md" data-testid="reactivate-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2"><RefreshCw className="w-5 h-5 text-emerald-600" /> Réactiver l'annonce</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#4B5563]">L'annonce redeviendra visible et ouverte aux candidatures, même si la date de début initiale est dépassée.</p>
            <div className="space-y-4">
              <div>
                <Label htmlFor="reactivate-expires">Visible jusqu'au</Label>
                <Input id="reactivate-expires" type="date" min={addDays(1)} value={reactivateData.expires_at}
                  onChange={(e) => setReactivateData(prev => ({ ...prev, expires_at: e.target.value }))} className="input-field" data-testid="reactivate-expires-input" />
                <div className="flex gap-2 mt-2">
                  {[15, 30, 60, 90].map(d => (
                    <button key={d} type="button" onClick={() => setReactivateData(prev => ({ ...prev, expires_at: addDays(d) }))}
                      className={`text-xs px-2 py-1 rounded-full border ${reactivateData.expires_at === addDays(d) ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]' : 'border-[#E5E7EB] text-[#4B5563] hover:border-[#1E3A8A]'}`} data-testid={`reactivate-quick-${d}`}>
                      +{d} jours
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="reactivate-start">Nouvelle date de début <span className="text-[#9CA3AF] font-normal">(optionnel)</span></Label>
                <Input id="reactivate-start" type="date" value={reactivateData.start_date}
                  onChange={(e) => setReactivateData(prev => ({ ...prev, start_date: e.target.value }))} className="input-field" data-testid="reactivate-start-input" />
                <p className="text-xs text-[#9CA3AF] mt-1">Laissez vide pour conserver la date actuelle ({job.start_date || '—'}).</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setReactivateOpen(false)} data-testid="reactivate-cancel">Annuler</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleReactivate} disabled={reactivating || !reactivateData.expires_at} data-testid="reactivate-confirm">
                {reactivating ? <Spinner className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Réactiver
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: candidature complète */}
        <Dialog open={!!viewingApp} onOpenChange={(open) => { if (!open) { setViewingApp(null); setQuickReply(''); } }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="application-detail-dialog">
            {viewingApp && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
                    {viewingApp.contractor_name || 'Sous-traitant'}
                    {viewingApp.status === 'accepted' && <Badge className="bg-emerald-100 text-emerald-700">Engagé</Badge>}
                    {viewingApp.status === 'rejected' && <Badge className="bg-red-100 text-red-700">Refusé</Badge>}
                    {viewingApp.status === 'pending' && <Badge className="bg-yellow-100 text-yellow-700">En attente</Badge>}
                  </DialogTitle>
                </DialogHeader>
                <p className="text-xs text-[#9CA3AF]">Reçue le {new Date(viewingApp.created_at).toLocaleDateString('fr-FR')}</p>
                <div className="p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB] text-sm text-[#374151] whitespace-pre-line leading-relaxed" data-testid="application-detail-message">
                  {viewingApp.message}
                </div>
                {viewingApp.proposed_budget && (
                  <p className="text-sm font-medium text-[#1E3A8A]">Budget proposé : {viewingApp.proposed_budget}€</p>
                )}
                <div className="border-t border-[#E5E7EB] pt-3" data-testid="quick-reply-box">
                  <Label htmlFor="quick-reply" className="text-sm font-semibold">Répondre rapidement à {viewingApp.contractor_name || 'ce sous-traitant'}</Label>
                  <Textarea id="quick-reply" rows={3} value={quickReply} onChange={(e) => setQuickReply(e.target.value)}
                    placeholder="Ex : Merci pour votre candidature, pouvez-vous passer sur site mardi pour une visite ?"
                    className="input-field mt-1" data-testid="quick-reply-input" />
                  <div className="flex justify-end mt-2">
                    <Button size="sm" className="btn-primary" onClick={handleQuickReply} disabled={sendingReply || !quickReply.trim()} data-testid="quick-reply-send">
                      {sendingReply ? <Spinner className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Envoyer
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 justify-end pt-2">
                  {viewingApp.contractor_id && (
                    <Link to={`/contractors/${viewingApp.contractor_id}`}>
                      <Button variant="outline" size="sm" data-testid="application-detail-profile">Voir le profil</Button>
                    </Link>
                  )}
                  <Link to={`/dashboard/messages/${viewingApp.contractor_id}`}>
                    <Button variant="outline" size="sm" data-testid="application-detail-message-btn">Envoyer un message</Button>
                  </Link>
                  {viewingApp.status === 'pending' && (
                    <>
                      <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => { handleUpdateAppStatus(viewingApp.id, 'accepted'); setViewingApp(null); }} data-testid="application-detail-accept">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Accepter
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => { handleUpdateAppStatus(viewingApp.id, 'rejected'); setViewingApp(null); }} data-testid="application-detail-reject">
                        <XCircle className="w-4 h-4 mr-1" /> Refuser
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* COMPARATEUR DE DEVIS */}
        {isJobOwner && (() => {
          const appsWithQuotes = applications.filter(a => a.quote_lines && a.quote_lines.length > 0);
          if (appsWithQuotes.length < 2) return null;

          // Build unified line descriptions across all quotes
          const allDescriptions = [];
          const seen = new Set();
          for (const app of appsWithQuotes) {
            for (const ql of app.quote_lines) {
              const desc = ql.description?.trim();
              if (desc && !seen.has(desc.toLowerCase())) {
                seen.add(desc.toLowerCase());
                allDescriptions.push(desc);
              }
            }
          }

          return (
            <Card className="mb-6" data-testid="quote-comparator">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#1E3A8A]" />
                  Comparateur de devis ({appsWithQuotes.length} sous-traitants)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-[#1E3A8A] text-white">
                        <th className="text-left p-2 font-medium border border-[#1E3A8A]/20 min-w-[180px]">Poste</th>
                        {appsWithQuotes.map(app => (
                          <th key={app.id} className="text-center p-2 font-medium border border-[#1E3A8A]/20 min-w-[120px]">
                            <div className="truncate">{app.contractor_name}</div>
                            {app.status === 'accepted' && <span className="text-xs text-emerald-200 block">Engagé</span>}
                          </th>
                        ))}
                        <th className="text-center p-2 font-medium border border-[#1E3A8A]/20 min-w-[80px]">Écart</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allDescriptions.map((desc, idx) => {
                        const prices = appsWithQuotes.map(app => {
                          const line = app.quote_lines.find(ql => ql.description?.trim().toLowerCase() === desc.toLowerCase());
                          return line ? (line.total || (line.quantity * line.unit_price) || 0) : null;
                        });
                        const validPrices = prices.filter(p => p !== null && p > 0);
                        const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
                        const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;
                        const ecart = maxPrice - minPrice;

                        return (
                          <tr key={`row-${desc}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'}>
                            <td className="p-2 border border-[#E5E7EB] font-medium text-[#374151]">{desc}</td>
                            {prices.map((price, pIdx) => (
                              <td key={`price-${idx}-${pIdx}`} className={`p-2 border border-[#E5E7EB] text-center font-mono ${
                                price !== null && price === minPrice && validPrices.length > 1
                                  ? 'bg-emerald-50 text-emerald-700 font-semibold'
                                  : price !== null && price === maxPrice && validPrices.length > 1 && ecart > 0
                                  ? 'bg-red-50 text-red-600'
                                  : 'text-[#374151]'
                              }`}>
                                {price !== null ? `${price.toFixed(2)}€` : '—'}
                              </td>
                            ))}
                            <td className={`p-2 border border-[#E5E7EB] text-center font-mono text-xs ${ecart > 0 ? 'text-amber-600 font-medium' : 'text-[#9CA3AF]'}`}>
                              {ecart > 0 ? `${ecart.toFixed(0)}€` : '='}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#F1F5F9] font-bold">
                        <td className="p-2 border border-[#E5E7EB] text-[#111827]">TOTAL HTVA</td>
                        {appsWithQuotes.map(app => {
                          const total = app.quote_lines.reduce((sum, l) => sum + (l.total || (l.quantity * l.unit_price) || 0), 0);
                          return (
                            <td key={app.id} className="p-2 border border-[#E5E7EB] text-center font-mono text-[#1E3A8A]">
                              {total.toFixed(2)}€
                            </td>
                          );
                        })}
                        <td className="p-2 border border-[#E5E7EB] text-center text-xs text-amber-600 font-mono">
                          {(() => {
                            const totals = appsWithQuotes.map(app => app.quote_lines.reduce((sum, l) => sum + (l.total || (l.quantity * l.unit_price) || 0), 0));
                            const diff = Math.max(...totals) - Math.min(...totals);
                            return diff > 0 ? `${diff.toFixed(0)}€` : '=';
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p className="text-xs text-[#6B7280] mt-3 flex items-center gap-2">
                  <span className="inline-block w-3 h-3 bg-emerald-50 border border-emerald-200 rounded"></span> Meilleur prix
                  <span className="inline-block w-3 h-3 bg-red-50 border border-red-200 rounded ml-2"></span> Prix le plus élevé
                </p>
              </CardContent>
            </Card>
          );
        })()}

        {/* Create Project - for job owner */}
        {isCompany && user?.id === job.company_id && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <Link to={`/dashboard/jobs/${id}/create-project`}>
                <Button className="btn-primary gap-2 w-full" data-testid="create-project-button">
                  <FolderKanban className="w-5 h-5" />
                  Créer un projet avec jalons
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Sidebar Info */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-heading font-bold text-lg mb-4">À propos de l'entreprise</h3>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#1E3A8A]/10 rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#1E3A8A]" />
              </div>
              <div>
                <p className="font-medium">{job.company_name}</p>
                <p className="text-sm text-[#4B5563]">{job.city}, {job.country}</p>
              </div>
            </div>
            <p className="text-sm text-[#4B5563]">
              {job.applications_count || 0} candidature(s) reçue(s)
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JobDetailPage;
